import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import AppShortcutOutlinedIcon from '@mui/icons-material/AppShortcutOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import API_BASE_URL from '../../config/api';

const PROFILE_URL = `${API_BASE_URL}/api/Users/profile`;
const CHANGE_PW_URL = `${API_BASE_URL}/api/Users/change-password`;
const TFA_BEGIN_URL = `${API_BASE_URL}/api/Users/2fa/begin-repair`;
const TFA_CONFIRM_URL = `${API_BASE_URL}/api/Users/2fa/confirm-repair`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('jwtToken')}` });

// Parse a response body safely — a 404/500 with an empty or HTML body would
// otherwise throw "Unexpected end of JSON input" on res.json().
async function readJsonSafe(res) {
  const text = await res.text();
  if (!text) {
    return { success: false, message: res.ok ? 'Empty response from server' : `Request failed (HTTP ${res.status})` };
  }
  try {
    return JSON.parse(text);
  } catch {
    return { success: false, message: `Unexpected response (HTTP ${res.status})` };
  }
}

function getInitials(first, last, email) {
  const f = (first || '').trim();
  const l = (last || '').trim();
  if (f || l) return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || '?';
  const local = (email || '').split('@')[0] || '?';
  return local.charAt(0).toUpperCase();
}

function ComingSoon() {
  return (
    <Chip
      label="Coming soon"
      size="small"
      variant="outlined"
      sx={{ height: 22, fontSize: 11, fontWeight: 600, color: 'text.disabled', borderColor: 'divider' }}
    />
  );
}

function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Change-password dialog state
  const [pwOpen, setPwOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  // Authenticator re-pair dialog state
  const [tfaOpen, setTfaOpen] = useState(false);
  const [tfaLoading, setTfaLoading] = useState(false);   // fetching the new QR
  const [tfaQr, setTfaQr] = useState('');
  const [tfaSecret, setTfaSecret] = useState('');
  const [tfaOtp, setTfaOtp] = useState('');
  const [tfaSubmitting, setTfaSubmitting] = useState(false);
  const [tfaError, setTfaError] = useState('');
  const [tfaSuccess, setTfaSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const res = await fetch(PROFILE_URL, { headers: authHeaders() });
        const data = await readJsonSafe(res);
        if (cancelled) return;
        if (data.success) setProfile(data);
        else setLoadError(data.message || 'Failed to load profile');
      } catch (e) {
        if (!cancelled) setLoadError(String(e));
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const displayName = useMemo(() => {
    if (!profile) return '';
    const n = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
    return n || (profile.email || '').split('@')[0] || 'User';
  }, [profile]);

  const role = useMemo(() => {
    if (!profile) return 'Team Member';
    if (profile.isOwner) return 'Owner';
    if (profile.isAdmin) return 'Administrator';
    return 'Team Member';
  }, [profile]);

  const roleColor = role === 'Owner' ? 'primary' : role === 'Administrator' ? 'secondary' : 'default';

  const memberSince = useMemo(() => {
    if (!profile?.createdDate) return null;
    const d = new Date(profile.createdDate);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }, [profile]);

  const initials = useMemo(
    () => (profile ? getInitials(profile.firstName, profile.lastName, profile.email) : '?'),
    [profile]
  );

  // ── Change password ───────────────────────────────────────────────────
  const newPwValid = newPw.length >= 8 && /[A-Z]/.test(newPw) && /[a-z]/.test(newPw) && /\d/.test(newPw);
  const canSubmitPw =
    currentPw && newPw && confirmPw && newPw === confirmPw && newPwValid && newPw !== currentPw;

  const resetPwDialog = () => {
    setCurrentPw(''); setNewPw(''); setConfirmPw('');
    setShowCurrent(false); setShowNew(false);
    setPwError(''); setPwSubmitting(false);
  };

  const openPwDialog = () => { resetPwDialog(); setPwSuccess(''); setPwOpen(true); };

  const submitPw = async () => {
    setPwError(''); setPwSubmitting(true);
    try {
      const res = await fetch(CHANGE_PW_URL, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: currentPw, newPassword: newPw }),
      });
      const data = await readJsonSafe(res);
      if (data.success) {
        setPwOpen(false);
        setPwSuccess('Your password has been changed.');
        resetPwDialog();
      } else {
        setPwError(data.message || 'Failed to change password');
        setPwSubmitting(false);
      }
    } catch (e) {
      setPwError(String(e));
      setPwSubmitting(false);
    }
  };

  // ── Authenticator re-pair ─────────────────────────────────────────────
  const openTfaDialog = async () => {
    setTfaError(''); setTfaOtp(''); setTfaQr(''); setTfaSecret('');
    setTfaSuccess(''); setTfaOpen(true); setTfaLoading(true);
    try {
      const res = await fetch(TFA_BEGIN_URL, { method: 'POST', headers: authHeaders() });
      const data = await readJsonSafe(res);
      if (data.success) { setTfaQr(data.qrCode); setTfaSecret(data.secretKey); }
      else setTfaError(data.message || 'Failed to start setup');
    } catch (e) { setTfaError(String(e)); }
    setTfaLoading(false);
  };

  const tfaOtpValid = /^\d{6}$/.test(tfaOtp.trim());

  const submitTfa = async () => {
    setTfaError(''); setTfaSubmitting(true);
    try {
      const res = await fetch(TFA_CONFIRM_URL, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ secretKey: tfaSecret, otp: tfaOtp.trim() }),
      });
      const data = await readJsonSafe(res);
      if (data.success) {
        setTfaOpen(false);
        setTfaSuccess(data.message || 'Authenticator updated.');
        // Reflect the now-configured state immediately.
        setProfile((p) => (p ? { ...p, is2FAVerified: true } : p));
      } else {
        setTfaError(data.message || 'Failed to update authenticator');
      }
    } catch (e) { setTfaError(String(e)); }
    setTfaSubmitting(false);
  };

  const renderInfoList = (items) => (
    <List disablePadding>
      {items.map((item, index) => (
        <React.Fragment key={item.label}>
          {index > 0 && <Divider component="li" variant="inset" />}
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon sx={{ color: 'text.secondary', minWidth: 44 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={<Typography variant="body2" color="text.secondary">{item.label}</Typography>}
              secondary={
                typeof item.value === 'string' ? (
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>{item.value}</Typography>
                ) : (
                  <Box sx={{ mt: 0.5 }}>{item.value}</Box>
                )
              }
              secondaryTypographyProps={{ component: 'div' }}
            />
          </ListItem>
        </React.Fragment>
      ))}
    </List>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
        <Skeleton variant="text" width={160} height={48} />
        <Skeleton variant="rounded" height={120} sx={{ my: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}><Skeleton variant="rounded" height={260} /></Grid>
          <Grid item xs={12} md={6}><Skeleton variant="rounded" height={260} /></Grid>
        </Grid>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>Profile</Typography>
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }

  const personalInfo = [
    { icon: <PersonOutlineIcon />, label: 'Full name', value: displayName },
    { icon: <EmailOutlinedIcon />, label: 'Email', value: profile.email },
    { icon: <PhoneIphoneOutlinedIcon />, label: 'Phone number', value: <ComingSoon /> },
    ...(memberSince ? [{ icon: <CalendarTodayOutlinedIcon />, label: 'Member since', value: memberSince }] : []),
  ];

  const securityInfo = [
    {
      icon: <VerifiedUserOutlinedIcon />,
      label: 'Two-factor authentication',
      value: profile.is2FAVerified
        ? <Chip label="Enabled" color="success" size="small" />
        : <Chip label="Not set up" color="warning" size="small" />,
    },
    {
      icon: <AppShortcutOutlinedIcon />,
      label: 'Authenticator app',
      value: profile.is2FAVerified ? 'Configured' : 'Not configured',
    },
    { icon: <PhoneIphoneOutlinedIcon />, label: 'SMS / phone backup', value: <ComingSoon /> },
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Profile</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Your account details and security settings.
      </Typography>

      {pwSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPwSuccess('')}>{pwSuccess}</Alert>
      )}
      {tfaSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setTfaSuccess('')}>{tfaSuccess}</Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Avatar sx={{ width: 88, height: 88, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 32, fontWeight: 700 }}>
              {initials}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }} noWrap>{displayName}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }} noWrap>{profile.email}</Typography>
              <Chip label={role} color={roleColor} variant={roleColor === 'default' ? 'outlined' : 'filled'} size="small" />
            </Box>
            <Button
              variant="contained"
              startIcon={<LockResetOutlinedIcon />}
              onClick={openPwDialog}
              sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
            >
              Change password
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Personal information</Typography>
              <Divider sx={{ mb: 1 }} />
              {renderInfoList(personalInfo)}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Name and email are managed by an administrator and can't be edited here.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Security</Typography>
              <Divider sx={{ mb: 1 }} />
              {renderInfoList(securityInfo)}
              <Stack direction="row" spacing={1.5} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                <Button variant="outlined" startIcon={<LockOutlinedIcon />} onClick={openPwDialog}>
                  Change password
                </Button>
                <Button variant="outlined" startIcon={<QrCode2OutlinedIcon />} onClick={openTfaDialog}>
                  {profile.is2FAVerified ? 'Update authenticator' : 'Set up authenticator'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Change password dialog */}
      <Dialog open={pwOpen} onClose={() => !pwSubmitting && setPwOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Change password</DialogTitle>
        <DialogContent>
          {pwError && <Alert severity="error" sx={{ mb: 2 }}>{pwError}</Alert>}
          <TextField
            fullWidth margin="dense" label="Current password"
            type={showCurrent ? 'text' : 'password'} value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowCurrent((s) => !s)} edge="end" size="small">
                    {showCurrent ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth margin="dense" label="New password"
            type={showNew ? 'text' : 'password'} value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            autoComplete="new-password"
            error={!!newPw && !newPwValid}
            helperText={
              newPw && !newPwValid
                ? 'At least 8 characters, with an uppercase, a lowercase, and a number'
                : ' '
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNew((s) => !s)} edge="end" size="small">
                    {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth margin="dense" label="Confirm new password"
            type={showNew ? 'text' : 'password'} value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            autoComplete="new-password"
            error={!!confirmPw && confirmPw !== newPw}
            helperText={confirmPw && confirmPw !== newPw ? 'Passwords do not match' : ' '}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPwOpen(false)} disabled={pwSubmitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitPw}
            disabled={!canSubmitPw || pwSubmitting}
            startIcon={pwSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {pwSubmitting ? 'Changing…' : 'Change password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Authenticator re-pair dialog */}
      <Dialog open={tfaOpen} onClose={() => !tfaSubmitting && setTfaOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {profile.is2FAVerified ? 'Update authenticator' : 'Set up authenticator'}
        </DialogTitle>
        <DialogContent>
          {tfaError && <Alert severity="error" sx={{ mb: 2 }}>{tfaError}</Alert>}
          {profile.is2FAVerified && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Setting up a new authenticator replaces your current one. Your old authenticator will stop working.
            </Alert>
          )}
          {tfaLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                1. Scan this QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, Authy…).
              </Typography>
              {tfaQr && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                  <Box
                    component="img"
                    src={tfaQr}
                    alt="Authenticator QR code"
                    sx={{ width: 200, height: 200, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: '#fff', p: 1 }}
                  />
                </Box>
              )}
              {tfaSecret && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 2 }}>
                  Can't scan? Enter this key manually:<br />
                  <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.primary', wordBreak: 'break-all' }}>
                    {tfaSecret}
                  </Box>
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                2. Enter the 6-digit code your app shows now:
              </Typography>
              <TextField
                fullWidth
                label="6-digit code"
                value={tfaOtp}
                onChange={(e) => setTfaOtp(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: 6, fontWeight: 600 } }}
                placeholder="000000"
                error={!!tfaOtp && !tfaOtpValid}
                helperText={tfaOtp && !tfaOtpValid ? 'Enter the 6 digits' : ' '}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTfaOpen(false)} disabled={tfaSubmitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitTfa}
            disabled={!tfaOtpValid || tfaSubmitting || tfaLoading || !tfaSecret}
            startIcon={tfaSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {tfaSubmitting ? 'Verifying…' : 'Verify & save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProfilePage;
